/**
 * @zendir/ui - Confirmation Dialog
 *
 * Convenience wrapper over Dialog for confirm/cancel actions.
 * Provides both a component API and a `useConfirm()` hook for imperative usage.
 *
 * Astro UX Compliance:
 * - Status-based alert colors
 * - Focus trap, escape to close
 * - Primary button on right (Astro convention)
 *
 * @example
 * ```tsx
 * // Hook API (imperative)
 * const confirm = useConfirm();
 * const ok = await confirm({
 *   title: 'Reset Component',
 *   message: 'This will reboot the spacecraft and suspend commands for 120 seconds.',
 *   status: 'caution',
 *   confirmLabel: 'Reset',
 *   destructive: true,
 * });
 * if (ok) doReset();
 *
 * // Component API (declarative)
 * <ConfirmDialog
 *   open={isOpen}
 *   onClose={() => setOpen(false)}
 *   onConfirm={handleReset}
 *   title="Delete All Data?"
 *   message="This cannot be undone."
 *   status="critical"
 *   destructive
 * />
 * ```
 */

import React, {
  memo,
  useState,
  useCallback,
  useRef,
  useEffect,
  useId,
  createContext,
  useContext,
} from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../theme";

// ─── Types ───────────────────────────────────────────────────────────────────

export type ConfirmStatus = "normal" | "caution" | "serious" | "critical";

export interface ConfirmOptions {
  /** Dialog title */
  title: string;
  /** Description text (string or React node) */
  message?: React.ReactNode;
  /** Status level (determines icon/border color) */
  status?: ConfirmStatus;
  /** Confirm button label (default: "Confirm") */
  confirmLabel?: string;
  /** Cancel button label (default: "Cancel") */
  cancelLabel?: string;
  /** Destructive action styling (red confirm button) */
  destructive?: boolean;
}

export interface ConfirmDialogProps extends ConfirmOptions {
  /** Open state */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Confirm handler */
  onConfirm: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const ConfirmDialog = memo(function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  status,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
}: ConfirmDialogProps): React.ReactElement | null {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const messageId = useId();
  const CONFIRM_DIALOG_Z_INDEX = 2147483200;

  useEffect(() => {
    if (!open) return;

    previousActiveElement.current = document.activeElement as HTMLElement;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key !== "Tab") return;
      const root = dialogRef.current;
      if (!root) return;

      const focusableElements = root.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      const focusable = Array.from(focusableElements).filter(
        (el) => !el.hasAttribute("disabled"),
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const focusTimer = window.setTimeout(() => {
      confirmRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      previousActiveElement.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  const statusColors: Record<string, string> = {
    normal: tokens.colors.status.normal,
    caution: tokens.colors.status.caution,
    serious: tokens.colors.status.serious,
    critical: tokens.colors.status.critical,
  };

  const accentColor = status
    ? statusColors[status]
    : tokens.colors.accent.primary;
  const confirmBg = destructive
    ? tokens.colors.status.critical
    : tokens.colors.accent.primary;

  // Status icon shapes (Astro UX dual-coded)
  const statusIcon =
    status &&
    (() => {
      const s = 32;
      const innerIcon = (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke={accentColor}
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
        </svg>
      );
      const baseStyle: React.CSSProperties = {
        width: s,
        height: s,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      };
      switch (status) {
        case "critical":
          // Triangle (using clip-path)
          return (
            <div
              style={{
                ...baseStyle,
                backgroundColor: `${accentColor}20`,
                border: `2px solid ${accentColor}`,
                clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
                paddingTop: 8,
              }}
            >
              {innerIcon}
            </div>
          );
        case "serious":
          // Diamond (rotated square)
          return (
            <div
              style={{
                ...baseStyle,
                backgroundColor: `${accentColor}20`,
                border: `2px solid ${accentColor}`,
                transform: "rotate(45deg)",
              }}
            >
              <div style={{ transform: "rotate(-45deg)" }}>{innerIcon}</div>
            </div>
          );
        case "caution":
          // Square (rounded)
          return (
            <div
              style={{
                ...baseStyle,
                backgroundColor: `${accentColor}20`,
                border: `2px solid ${accentColor}`,
                borderRadius: "4px",
              }}
            >
              {innerIcon}
            </div>
          );
        default:
          // Circle (normal, standby, off)
          return (
            <div
              style={{
                ...baseStyle,
                backgroundColor: `${accentColor}20`,
                border: `2px solid ${accentColor}`,
                borderRadius: "50%",
              }}
            >
              {innerIcon}
            </div>
          );
      }
    })();

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: CONFIRM_DIALOG_Z_INDEX,
          backgroundColor: isTransparentTheme
            ? `${tokens.colors.background.base}80`
            : `${tokens.colors.background.base}B3`,
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          animation: `zendir-confirm-backdrop 200ms ${tokens.animation.easing.default}`,
        }}
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={message ? messageId : undefined}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: CONFIRM_DIALOG_Z_INDEX + 1,
          width: "100%",
          maxWidth: "min(440px, calc(100vw - 32px))",
          backgroundColor: isTransparentTheme
            ? "rgba(15, 12, 30, 0.9)"
            : tokens.colors.background.surface,
          border: `1px solid ${tokens.colors.border.default}`,
          borderRadius: tokens.borderRadius.lg,
          boxShadow: tokens.shadows.xl,
          fontFamily: tokens.typography.fontFamily.primary,
          animation: `zendir-confirm-enter 250ms ${tokens.animation.easing.default}`,
          overflow: "hidden",
          ...(isTransparentTheme
            ? {
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
              }
            : {}),
        }}
      >
        {/* Status accent bar */}
        {status && (
          <div
            style={{
              height: 3,
              backgroundColor: accentColor,
              width: "100%",
            }}
          />
        )}

        {/* Content */}
        <div
          style={{
            padding: `${tokens.spacing.lg} ${tokens.spacing.lg} ${tokens.spacing.md}`,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: tokens.spacing.md,
              alignItems: "flex-start",
            }}
          >
            {statusIcon}
            <div style={{ flex: 1 }}>
              <div
                id={titleId}
                style={{
                  fontSize: tokens.typography.fontSize.md,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  color: tokens.colors.text.primary,
                  lineHeight: tokens.typography.lineHeight.tight,
                }}
              >
                {title}
              </div>
              {message && (
                <div
                  id={messageId}
                  style={{
                    fontSize: tokens.typography.fontSize.sm,
                    color: tokens.colors.text.secondary,
                    marginTop: tokens.spacing.sm,
                    lineHeight: tokens.typography.lineHeight.normal,
                  }}
                >
                  {message}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions — Astro UX: primary on right */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: tokens.spacing.sm,
            padding: `${tokens.spacing.md} ${tokens.spacing.lg} ${tokens.spacing.lg}`,
            borderTop: tokens.borders.divider,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: `0 ${tokens.spacing.md}`,
              minHeight: tokens.elementSize.lg,
              fontSize: tokens.typography.fontSize.sm,
              fontWeight: tokens.typography.fontWeight.medium,
              fontFamily: tokens.typography.fontFamily.primary,
              color: tokens.colors.text.primary,
              backgroundColor: tokens.colors.background.base,
              border: `1px solid ${tokens.colors.border.muted}`,
              borderRadius: tokens.borderRadius.md,
              cursor: "pointer",
              transition: tokens.animation.fast,
            }}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            onClick={() => {
              onConfirm();
              onClose();
            }}
            style={{
              padding: `0 ${tokens.spacing.md}`,
              minHeight: tokens.elementSize.lg,
              fontSize: tokens.typography.fontSize.sm,
              fontWeight: tokens.typography.fontWeight.semibold,
              fontFamily: tokens.typography.fontFamily.primary,
              color: tokens.colors.text.inverse || "#fff",
              backgroundColor: confirmBg,
              border: "none",
              borderRadius: tokens.borderRadius.md,
              cursor: "pointer",
              transition: tokens.animation.fast,
              boxShadow: `0 2px 8px ${confirmBg}40`,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes zendir-confirm-backdrop { from { opacity: 0; } to { opacity: 1; } }
        @keyframes zendir-confirm-enter {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>
    </>,
    document.body,
  );
});

// ─── useConfirm Hook ─────────────────────────────────────────────────────────

interface ConfirmState extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

const ConfirmContext = createContext<{
  requestConfirm: (options: ConfirmOptions) => Promise<boolean>;
} | null>(null);

/**
 * Imperative confirmation hook.
 * Must be used within `<ConfirmProvider>`.
 *
 * @example
 * ```tsx
 * const confirm = useConfirm();
 * const ok = await confirm({ title: 'Delete?', status: 'critical', destructive: true });
 * ```
 */
export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return ctx.requestConfirm;
}

/**
 * Provider for the useConfirm() hook.
 * Renders the confirmation dialog as needed.
 */
export const ConfirmProvider = memo(function ConfirmProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [state, setState] = useState<ConfirmState | null>(null);

  const requestConfirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        setState({ ...options, resolve });
      });
    },
    [],
  );

  const handleClose = useCallback(() => {
    state?.resolve(false);
    setState(null);
  }, [state]);

  const handleConfirm = useCallback(() => {
    state?.resolve(true);
    setState(null);
  }, [state]);

  return (
    <ConfirmContext.Provider value={{ requestConfirm }}>
      {children}
      {state && (
        <ConfirmDialog
          open={true}
          onClose={handleClose}
          onConfirm={handleConfirm}
          title={state.title}
          message={state.message}
          status={state.status}
          confirmLabel={state.confirmLabel}
          cancelLabel={state.cancelLabel}
          destructive={state.destructive}
        />
      )}
    </ConfirmContext.Provider>
  );
});

export default ConfirmDialog;
